import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FlowService } from "../../../../../service/FlowService";
import { FormsService } from "../../../../../service/FormService";
import { AuthService } from "../../../../../service/AuthService";
import { useAuthStore } from "../../../../../store/storeAuth";
import { useNotificationStore, NotificationType } from "../../../../../store/storeNotifications";
import useFlowsStore from "../../../../../store/storeFlows";
import { getAPIErrorMessage } from "../../../../../helpers/helpers";
import { CreateItemType } from "../../types";

interface UseItemHandlersProps {
  type: CreateItemType;
  name: string;
  selectedSpaceId: string;
  ensureFolderExists: (folderName: string, spaceId?: string) => Promise<string | null>;
  loadSpaces: () => Promise<void>;
  resetForm: () => void;
  onHide: () => void;
  setVectorMemorySpaceId: (id: string | undefined) => void;
  setShowVectorModal: (show: boolean) => void;
  setIsCreating: (creating: boolean) => void;
  setIsCreateFormLoading: (loading: boolean) => void;
  setShowInterfaceModal: (show: boolean) => void;
}

export const useItemHandlers = ({
  type,
  name,
  selectedSpaceId,
  ensureFolderExists,
  loadSpaces,
  resetForm,
  onHide,
  setVectorMemorySpaceId,
  setShowVectorModal,
  setIsCreating,
  setIsCreateFormLoading,
  setShowInterfaceModal,
}: UseItemHandlersProps) => {
  const navigate = useNavigate();
  const { addFlow } = useFlowsStore((state) => state);
  const { setAuthUser: setAuthUserStore } = useAuthStore((state) => state);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const handleCreateAgent = useCallback(async () => {
    if (!name.trim()) {
      setNotification({
        type: NotificationType.Error,
        message: "Please enter a name",
      });
      return;
    }

    setIsCreating(true);
    try {
      const spaceId = selectedSpaceId === "private" ? undefined : selectedSpaceId;
      const folderId = await ensureFolderExists("Agents", spaceId);
      if (!folderId) {
        setIsCreating(false);
        return;
      }

      const response = await FlowService.createFlow({
        name: name.trim(),
        folderId: folderId,
        type: "flow",
      });

      addFlow(response.data);
      await loadSpaces();
      const userRes = await AuthService.getMe();
      setAuthUserStore(userRes?.data);

      window.dispatchEvent(new CustomEvent("sidebar-refresh"));

      setNotification({
        type: NotificationType.Success,
        message: "Agent created successfully",
      });

      await new Promise((resolve) => setTimeout(resolve, 500));
      navigate(`/dashboard-beta/agent/${response.data.id}`);
      onHide();
      resetForm();
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error as any),
      });
    } finally {
      setIsCreating(false);
    }
  }, [
    name,
    selectedSpaceId,
    ensureFolderExists,
    loadSpaces,
    resetForm,
    onHide,
    setIsCreating,
    addFlow,
    setAuthUserStore,
    setNotification,
    navigate,
  ]);

  const handleCreateFluxbot = useCallback(async () => {
    if (!name.trim()) {
      setNotification({
        type: NotificationType.Error,
        message: "Please enter a name",
      });
      return;
    }

    setIsCreating(true);
    try {
      const spaceId = selectedSpaceId === "private" ? undefined : selectedSpaceId;
      const folderId = await ensureFolderExists("Fluxbots", spaceId);
      if (!folderId) {
        setIsCreating(false);
        return;
      }

      const response = await FlowService.createFlow({
        name: name.trim(),
        folderId: folderId,
        type: "chat",
      });

      addFlow(response.data);
      await loadSpaces();
      const userRes = await AuthService.getMe();
      setAuthUserStore(userRes?.data);

      window.dispatchEvent(new CustomEvent("sidebar-refresh"));

      setNotification({
        type: NotificationType.Success,
        message: "Fluxbot created successfully",
      });

      await new Promise((resolve) => setTimeout(resolve, 500));
      navigate(`/dashboard-beta/agent/${response.data.id}`);
      onHide();
      resetForm();
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error as any),
      });
    } finally {
      setIsCreating(false);
    }
  }, [
    name,
    selectedSpaceId,
    ensureFolderExists,
    loadSpaces,
    resetForm,
    onHide,
    setIsCreating,
    addFlow,
    setAuthUserStore,
    setNotification,
    navigate,
  ]);

  const handleCreateVectorMemory = useCallback(async () => {
    setIsCreating(true);
    try {
      const actualSpaceId =
        selectedSpaceId === "private" || selectedSpaceId === ""
          ? undefined
          : selectedSpaceId;
      const folderId = await ensureFolderExists("Vector Memory", actualSpaceId);
      if (!folderId) {
        setIsCreating(false);
        return;
      }

      setVectorMemorySpaceId(actualSpaceId);
      setShowVectorModal(true);
      resetForm();
      setIsCreating(false);
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error as any),
      });
      setIsCreating(false);
    }
  }, [
    selectedSpaceId,
    ensureFolderExists,
    resetForm,
    setIsCreating,
    setVectorMemorySpaceId,
    setShowVectorModal,
    setNotification,
  ]);

  const handleCreateInterface = useCallback(
    async (
      interfaceName: string,
      useAgent: boolean,
      flowId: string,
      spaceId?: string,
    ) => {
      setIsCreateFormLoading(true);
      try {
        const actualSpaceId = spaceId === "private" ? undefined : spaceId;
        const folderId = await ensureFolderExists("Agents", actualSpaceId);
        if (!folderId) {
          setIsCreateFormLoading(false);
          return;
        }

        const formResponse = await FormsService.createForm({
          name: interfaceName.trim(),
          flowId: flowId,
          folderId: folderId,
          useAgent,
        });

        await loadSpaces();
        const userRes = await AuthService.getMe();
        setAuthUserStore(userRes?.data);

        window.dispatchEvent(new CustomEvent("sidebar-refresh"));

        setNotification({
          type: NotificationType.Success,
          message: "Interface created successfully",
        });

        await new Promise((resolve) => setTimeout(resolve, 300));
        navigate(`/dashboard-beta/interface/${formResponse.data.id}`);
        setShowInterfaceModal(false);
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as any),
        });
      } finally {
        setIsCreateFormLoading(false);
      }
    },
    [
      ensureFolderExists,
      loadSpaces,
      setShowInterfaceModal,
      setIsCreateFormLoading,
      setAuthUserStore,
      setNotification,
      navigate,
    ],
  );

  const handleCreate = useCallback(async () => {
    if (type === "agent") {
      await handleCreateAgent();
    } else if (type === "fluxbot") {
      await handleCreateFluxbot();
    } else if (type === "vectorMemory") {
      await handleCreateVectorMemory();
    } else if (type === "interface") {
      setShowInterfaceModal(true);
    }
  }, [type, handleCreateAgent, handleCreateFluxbot, handleCreateVectorMemory, setShowInterfaceModal]);

  return {
    handleCreate,
    handleCreateInterface,
  };
};
